import React, { useEffect, useMemo, useState } from "react"
import Modal from "../../components/Modal"
import { evaluateFormula } from "../../utils/formulaEvaluator"
import { useSelector } from "react-redux"
import { selectWebsiteData } from "../../redux/general/selectors"
import { BASE_URL } from "../Alerts/Alerts"
import DynamicComponentRenderer from "../DynamicReactComponent/DynamicComponentRenderer"
import { useLocalization } from "../../providers/LocalizationProvider"

const CompareAssessmentModal = ({
    open,
    onClose,
    selectedRows = [],
    schema,
    formTitle,
    patientName,
}) => {
    const websiteData = useSelector(selectWebsiteData)
    const [showPrintRenderer, setShowPrintRenderer] = useState(false)
    const { voiceiiTags } = useLocalization()
    useEffect(() => {
        if (showPrintRenderer) {
            const timer = setTimeout(() => {
                setShowPrintRenderer(false)
            }, 1000)

            return () => clearTimeout(timer)
        }
    }, [showPrintRenderer])

    const sortedSelectedRows = useMemo(() => {
        return [...(selectedRows || [])].sort((a, b) => {
            // =========================
            // SAFE DATE PARSING
            // =========================

            const parsedDateA = new Date(a?.created_at).getTime()
            const parsedDateB = new Date(b?.created_at).getTime()

            // =========================
            // HANDLE INVALID DATES
            // =========================

            const dateA = Number.isNaN(parsedDateA) ? 0 : parsedDateA
            const dateB = Number.isNaN(parsedDateB) ? 0 : parsedDateB

            // =========================
            // ASCENDING ORDER
            // OLDEST -> NEWEST
            // =========================

            return dateA - dateB
        })
    }, [selectedRows])

    const comparisonRows = useMemo(() => {
        if (!sortedSelectedRows?.length || !schema?.fields?.length) {
            return []
        }

        const rows = []

        // =========================
        // FORM REFERENCE FIELDS
        // =========================

        const formReferenceFields = schema.fields.filter(
            (field) => field?.type === "form-reference-number",
        )

        schema.fields.forEach((field) => {
            // =========================
            // SKIP FILE / IMAGE FIELDS
            // =========================

            if (["image", "images", "file", "files"].includes(field?.type)) {
                return
            }

            // =========================
            // SKIP FORM REF FIELD
            // (render separately below)
            // =========================

            if (field?.type === "form-reference-number") {
                return
            }

            // =========================
            // SECTION HEADER
            // =========================

            if (field?.type === "section-header") {
                rows.push({
                    type: "section",
                    label: field?.label_key,
                    description: field?.description_key,
                })

                return
            }

            // =========================
            // RADIO GRID
            // =========================

            if (field?.type === "radio-grid" && field?.rows?.length) {
                rows.push({
                    type: "group",
                    label: field?.label_key,
                })

                field.rows.forEach((rowField) => {
                    const values = sortedSelectedRows.map((item) => {
                        const submissionData = item?.data || {}

                        return submissionData?.[field?.key]?.[rowField?.key] ?? "-"
                    })

                    rows.push({
                        type: "field",
                        label: rowField?.label_key,
                        description: rowField?.description_key,
                        values,
                    })
                })

                return
            }

            // =========================
            // NORMAL FIELD
            // =========================

            const values = sortedSelectedRows.map((item) => {
                const submissionData = item?.data || {}

                const value = submissionData?.[field?.key]

                // =========================
                // CHECKBOX FORMAT
                // =========================

                if (field?.type === "checkbox") {
                    return value ? "✓" : "✗"
                }

                return value ?? "-"
            })

            rows.push({
                type: "field",
                label: field?.label_key,
                values,
            })
        })

        // =========================
        // ADD FORM REF FIELDS
        // =========================

        formReferenceFields.forEach((field) => {
            rows.push({
                type: "form-reference",
                label: field?.label_key || voiceiiTags("Form Ref No."),
                value: field?.default_value || field?.placeholder_key || "-",
            })
        })

        return rows
    }, [sortedSelectedRows, schema])

    return (
        <>
            <Modal
                fullWidth
                maxWidth="xl"
                status={open}
                disableSave
                onClose={onClose}
                modalContentStyles={{
                    padding: "40px",
                }}
            >
                <div className="compare-modal">
                    {/* ================= HEADER ================= */}

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="m-0">{voiceiiTags("Compare Assessments")}</h2>

                        <button
                            className="btn btn-secondary e-print-comparison-btn"
                            onClick={() => {
                                setShowPrintRenderer(true)
                            }}
                        >
                            {voiceiiTags("Print Comparison")}
                        </button>
                    </div>

                    {/* ================= TABLE ================= */}

                    <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th
                                        style={{
                                            minWidth: 200,
                                        }}
                                    >
                                        {voiceiiTags("Field")}
                                    </th>

                                    {sortedSelectedRows.map((item, index) => (
                                        <th
                                            key={item?.id || index}
                                            style={{
                                                minWidth: 180,
                                            }}
                                        >
                                            <div className="fw-bold">
                                                {Number.isNaN(new Date(item?.created_at).getTime())
                                                    ? "-"
                                                    : new Date(item?.created_at)
                                                          .toLocaleString("en-GB", {
                                                              day: "2-digit",
                                                              month: "2-digit",
                                                              year: "numeric",
                                                              hour: "2-digit",
                                                              minute: "2-digit",
                                                              second: "2-digit",
                                                              hour12: true,
                                                          })
                                                          .replace(",", "")}
                                            </div>

                                            <small className="text-muted">
                                                {item?.revised_by_name || "-"}
                                            </small>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {comparisonRows.map((row, index) => {
                                    // ================= SECTION =================

                                    if (row.type === "section") {
                                        return (
                                            <tr key={`section-${index}`}>
                                                <td
                                                    colSpan={sortedSelectedRows.length + 1}
                                                    style={{
                                                        background: "#f5f7fb",
                                                        fontWeight: 700,
                                                        fontSize: 16,
                                                    }}
                                                >
                                                    <div>{row.label}</div>

                                                    {row.description && (
                                                        <div
                                                            className="text-muted mt-1"
                                                            style={{
                                                                fontSize: 13,
                                                                fontWeight: 400,
                                                            }}
                                                        >
                                                            {row.description}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    }

                                    // ================= GROUP =================

                                    if (row.type === "group") {
                                        return (
                                            <tr key={`group-${index}`}>
                                                <td
                                                    colSpan={sortedSelectedRows.length + 1}
                                                    style={{
                                                        background: "#fafafa",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {row.label}
                                                </td>
                                            </tr>
                                        )
                                    }

                                    // ================= FORM REF =================

                                    if (row.type === "form-reference") {
                                        return (
                                            <tr key={`form-ref-${index}`}>
                                                <td
                                                    colSpan={sortedSelectedRows.length + 1}
                                                    className="p-3"
                                                >
                                                    <div
                                                        className="d-flex justify-content-between align-items-center px-3 py-2"
                                                        style={{
                                                            background: "#fafafa",
                                                        }}
                                                    >
                                                        <span className="text-muted small">
                                                            {row.label}
                                                        </span>

                                                        <span className="fw-semibold">
                                                            {row.value}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    }

                                    // ================= FIELD =================

                                    const hasDifference = false

                                    return (
                                        <tr key={`field-${index}`}>
                                            <td>
                                                <div className="fw-semibold">{row.label}</div>

                                                {row.description && (
                                                    <div
                                                        className="text-muted mt-1"
                                                        style={{
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {row.description}
                                                    </div>
                                                )}
                                            </td>

                                            {row.values.map((value, i) => (
                                                <td
                                                    key={i}
                                                    style={{
                                                        background: hasDifference
                                                            ? "#fff3cd"
                                                            : "transparent",
                                                        fontWeight: hasDifference ? 700 : 400,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {value !== undefined &&
                                                    value !== null &&
                                                    value !== ""
                                                        ? String(value)
                                                        : "-"}
                                                </td>
                                            ))}
                                        </tr>
                                    )
                                })}

                                {/* ================= EMPTY ================= */}

                                {!comparisonRows.length && (
                                    <tr>
                                        <td
                                            colSpan={sortedSelectedRows.length + 1}
                                            className="text-center py-5"
                                        >
                                            {voiceiiTags("No comparison data found")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
            {showPrintRenderer && (
                <DynamicComponentRenderer
                    componentKey="comparison_print"
                    componentName="comparison_print"
                    componentProps={{
                        title: formTitle || voiceiiTags("Comparison"),
                        fields: schema?.fields || [],
                        submissions: sortedSelectedRows || [],
                        patientName: patientName?.toUpperCase() || "",
                        organizationName: localStorage.getItem("app_name"),
                        logoUrl: `${BASE_URL}${websiteData?.logo}`,
                        maxColumns: 5,
                        evaluateFormula,
                    }}
                />
            )}
        </>
    )
}

export default CompareAssessmentModal
